import _ from 'underscore';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { filterQuery } from '@culture/common-helpers/url';
import Animation from 'components/Animation';
import ButtonFilter from 'components/ButtonFilter';
import Calendar from 'components/Calendar';
import CustomSelect from 'components/CustomSelect';
import { Controls, ControlsBlock, ControlsRow } from 'components/Controls';
import Search from 'components/Search';

import CatalogPropTypes from 'utils/propTypes/catalogs';
import ClearButton from 'components/ClearButton';

const Filters = ({ items, onChange, onReset, values }) => {
	const [showSearch, setShowSearch] = useState(false);
	const searchButtonRef = useRef(null);
	const options = useMemo(() => {
		const mappingFn = ({
			values: filterValues = [],
			key,
			valueField = 'name'
		}) => {
			const uniqValues = _.uniq(
				filterValues.map((item) => ({
					title: item.title || String(item),
					[valueField]: item[valueField] || String(item)
				})),
				false,
				(item) => item[valueField]
			);
			return [key, uniqValues];
		};

		const mappedEntries = items.map(mappingFn);

		return _.object(mappedEntries);
	}, [items]);

	const getSelectiveFilterItem = (item) =>
		!_.isEmpty(item.values) ? (
			<ControlsBlock hidden={_.isEmpty(item.values)} key={item.key}>
				<CustomSelect
					selectedValue={values[item.key] && String(values[item.key])}
					searchable={item.searchable ?? true}
					options={options[item.key]}
					name={item.key}
					onChange={onChange}
					ariaLabel={item?.ariaLabel}
					defaultTitle={item.title}
					controlElement={
						<ButtonFilter
							icon={item.icon}
							active
							size="mobileSmall"
							data-aria-id={item.key}
							ariaHideTextNode
						/>
					}
					valueField={item.valueField}
					resetOption={item.resetOption}
				/>
			</ControlsBlock>
		) : null;

	const getButtonFilterItem = (item) => (
		<ControlsBlock hidden={!item.isShow} key={item.key}>
			<ButtonFilter
				onClick={onChange}
				name={item.key}
				value={item.value}
				text={item.title}
				icon={item.icon}
				active={item.active}
				ariaLabel={`${item?.ariaLabel}: ${
					item.active ? 'Включено' : 'Отключено'
				}`}
				size="mobileSmall"
				ariaHideTextNode
			/>
		</ControlsBlock>
	);

	const getDateFilterItem = (item) => (
		<ControlsBlock
			hidden={_.isEmpty(item.values) && _.isEmpty(item.dates)}
			key={item.key}
		>
			<Calendar {...item} onChange={onChange} />
		</ControlsBlock>
	);

	const showSearchFunc = useCallback(() => setShowSearch(true), []);
	const handleCloseClick = () => {
		setShowSearch(false);
		searchButtonRef.current?.focus();
	};

	const handleSearchChange = (value) => {
		setShowSearch(false);
		onChange('query', value);
		searchButtonRef.current?.focus();
	};

	const isRubricChild100 = values.rubric === 'cinema/movies/family/child-100';

	const filterRows = isRubricChild100
		? items.reduce(
				(memo, value) => {
					if (
						(value.key === 'schoolClass' || value.key === 'genre') &&
						!_.isEmpty(value.values)
					) {
						memo.second.push(value);
					} else {
						memo.first.push(value);
					}

					return memo;
				},
				{
					first: [],
					second: []
				}
		  )
		: { first: items || [] };

	const getFilterItem = (item) => {
		if (item.type === 'select') {
			return getSelectiveFilterItem(item);
		}
		if (item.type === 'button') {
			return getButtonFilterItem(item);
		}
		if (item.type === 'date') {
			return getDateFilterItem(item);
		}

		return null;
	};

	const filterItemsFirst = filterRows.first?.map((item) => getFilterItem(item));

	const filterItemsSecond = filterRows.second?.map((item) =>
		getFilterItem(item)
	);

	return (
		<Controls>
			<ControlsRow>
				{filterItemsFirst}
				<ControlsBlock key="search">
					<ButtonFilter
						active
						text={values.query || 'Поиск'}
						icon="search"
						onClick={showSearchFunc}
						size="mobileSmall"
						ref={searchButtonRef}
					/>
					<Animation show={showSearch}>
						<Search
							onChange={handleSearchChange}
							onRequestClose={handleCloseClick}
						/>
					</Animation>
				</ControlsBlock>
				<ControlsBlock hidden={_.isEmpty(filterQuery(values))} key="close">
					<ClearButton
						onClick={onReset}
						ariaHidden={_.isEmpty(filterQuery(values))}
					/>
				</ControlsBlock>
			</ControlsRow>

			{!_.isEmpty(filterItemsSecond) && (
				<ControlsRow second>{filterItemsSecond}</ControlsRow>
			)}
		</Controls>
	);
};

Filters.propTypes = {
	items: PropTypes.arrayOf(CatalogPropTypes.filterItem),
	onChange: PropTypes.func,
	onReset: PropTypes.func,
	values: PropTypes.object
};

export default Filters;
